import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { toast } from 'sonner';
import { AdminLayout } from '@/components/AdminLayout';
import { StatusBadge } from '@/components/StatusBadge';
import { Package, MapPin, CheckCircle2, Clock3, XCircle, LayoutDashboard, List, TrendingUp, MessageCircle, Send, Loader2, Truck, Route } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

const STATUS_COLORS = {
  'Notificada': '#2563EB',
  'Notificada': '#2563EB',
  'Asignada': '#7C3AED',
  'En Ruta': '#D97706',
  'Recogida': '#0891B2',
  'Completada': '#16A34A',
  'Cancelada': '#DC2626',
  'Cancelada': '#DC2626',
  'Completada': '#16A34A',
};

function StatCard({ label, value, color, icon: Icon }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-slate-500">{label}</span>
        <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}20` }}>
          <Icon size={14} style={{ color }} />
        </div>
      </div>
      <p className="text-2xl font-bold" style={{ color }}>{value}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [mapData, setMapData] = useState([]);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/admin/stats`, { headers: authHeaders() })
      .then(r => setStats(r.data))
      .catch(console.error);
    axios.get(`${API}/admin/map-data`, { headers: authHeaders() })
      .then(r => setMapData(r.data))
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;
    // Fix leaflet icons
    delete L.Icon.Default.prototype._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
      iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
    });
    const map = L.map(mapRef.current, { center: [40.473, -3.8715], zoom: 13 });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap',
      maxZoom: 19,
    }).addTo(map);
    mapInstanceRef.current = map;
    return () => { map.remove(); mapInstanceRef.current = null; };
  }, []);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || mapData.length === 0) return;
    mapData.forEach(s => {
      if (!s.latitude || !s.longitude) return;
      const color = STATUS_COLORS[s.estado] || '#374B67';
      const marker = L.circleMarker([s.latitude, s.longitude], {
        radius: 8, color, fillColor: color, fillOpacity: 0.8, weight: 2,
      }).addTo(map);
      marker.bindPopup(`
        <div style="font-family:Inter,sans-serif;min-width:180px">
          <p style="font-weight:700;color:#374B67;margin:0 0 4px">${s.ticket_code}</p>
          <p style="font-size:12px;color:#475569;margin:0 0 2px">${s.zona}</p>
          <p style="font-size:12px;color:#475569;margin:0 0 4px">${s.tipo_enseres}</p>
          <span style="display:inline-block;background:${color}20;color:${color};border:1px solid ${color}40;border-radius:4px;padding:2px 8px;font-size:11px;font-weight:600">${s.estado}</span>
        </div>
      `);
    });
  }, [mapData]);

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Page title */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-semibold text-[#374B67]">Dashboard</h1>
            <p className="text-sm text-slate-500">Vista general del servicio de recogida</p>
          </div>
          <div className="flex gap-2">
            <Link
              to="/admin/solicitudes"
              className="flex items-center gap-2 bg-[#009940] hover:bg-[#007F35] text-white px-4 py-2 rounded-xl text-sm font-semibold transition-colors"
              data-testid="dashboard-solicitudes-link"
            >
              <List size={15} /> Ver todas
            </Link>
          </div>
        </div>

        {/* Stats grid */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <StatCard label="Total solicitudes" value={stats.total} color="#374B67" icon={Package} />
            <StatCard label="Notificadas" value={stats.por_estado['Notificada'] || 0} color="#2563EB" icon={CheckCircle2} />
            <StatCard label="Asignadas" value={(stats.por_estado['Asignada'] || 0) + (stats.por_estado['En Ruta'] || 0)} color="#7C3AED" icon={Clock3} />
            <StatCard label="Completadas" value={stats.por_estado['Completada'] || 0} color="#16A34A" icon={TrendingUp} />
          </div>
        )}

        {/* Map + Recent */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
          {/* Map */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100">
              <h2 className="font-semibold text-[#374B67] text-sm">Mapa de solicitudes</h2>
              <p className="text-xs text-slate-500">{mapData.length} puntos activos</p>
            </div>
            <div ref={mapRef} className="admin-map-container" style={{ height: '420px' }} data-testid="admin-map" />
          </div>

          {/* Recent */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
            <div className="px-5 py-4 border-b border-slate-100">
              <h2 className="font-semibold text-[#374B67] text-sm">Recientes</h2>
            </div>
            <div className="divide-y divide-slate-100">
              {stats?.recent?.map(s => (
                <Link
                  key={s.id}
                  to={`/admin/solicitudes/${s.id}`}
                  className="flex items-center justify-between px-5 py-3 hover:bg-slate-50 transition-colors"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-mono font-medium text-[#374B67] truncate">{s.ticket_code}</p>
                    <p className="text-xs text-slate-500 truncate">{s.zona} · {s.tipo_enseres}</p>
                  </div>
                  <StatusBadge status={s.estado} />
                </Link>
              ))}
              {(!stats?.recent || stats.recent.length === 0) && (
                <div className="px-5 py-8 text-center text-sm text-slate-400">Sin solicitudes aún</div>
              )}
            </div>
            {stats?.recent && stats.recent.length > 0 && (
              <div className="px-5 py-3 border-t border-slate-100">
                <Link to="/admin/solicitudes" className="text-sm text-[#009940] font-medium hover:underline">
                  Ver todas las solicitudes &rarr;
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Zone breakdown */}
        {stats && (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
            {Object.entries(stats.por_zona).map(([zona, count]) => (
              <div key={zona} className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                <div className="flex items-center gap-2 mb-2">
                  <MapPin size={14} className="text-[#69C7EA]" />
                  <span className="text-sm font-medium text-[#374B67]">{zona}</span>
                </div>
                <p className="text-2xl font-bold text-[#374B67]">{count}</p>
                <p className="text-xs text-slate-400">solicitudes</p>
              </div>
            ))}
          </div>
        )}

        {/* WhatsApp Bot Status */}
        <WhatsAppTestPanel />
      </div>
    </AdminLayout>
  );
}

function WhatsAppTestPanel() {
  const [testPhone, setTestPhone] = useState('');
  const [sending, setSending] = useState(false);

  const handleTest = async () => {
    if (!testPhone) { toast.error('Introduce un número de teléfono'); return; }
    setSending(true);
    try {
      const resp = await axios.post(`${API}/admin/test-whatsapp`,
        { phone: testPhone },
        { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } }
      );
      if (resp.data.sent) {
        toast.success(`Mensaje de prueba enviado a ${testPhone}`);
      } else {
        toast.error('No se pudo enviar el mensaje. Verifica que el número esté en el WABA.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al enviar mensaje de prueba');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-4 bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-[#25D366]/10 flex items-center justify-center">
          <MessageCircle size={16} className="text-[#25D366]" />
        </div>
        <div>
          <h2 className="font-semibold text-[#374B67] text-sm">Bot WhatsApp</h2>
          <p className="text-xs text-slate-500">Número: +14782426755 · Webhook activo</p>
        </div>
        <span className="ml-auto flex items-center gap-1.5 text-xs text-green-700 bg-green-50 border border-green-200 rounded-full px-3 py-1 font-medium">
          <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>Activo
        </span>
      </div>

      <div className="bg-slate-50 rounded-xl p-4 mb-4 text-xs text-slate-600 space-y-1">
        <p className="font-semibold text-slate-700 mb-2">Comandos disponibles para ciudadanos:</p>
        <p>🔍 <code className="bg-white px-1 rounded">CONSULTA MAJ-YYYYMMDD-XXXX</code> — Consultar estado de solicitud</p>
        <p>📝 <code className="bg-white px-1 rounded">NUEVA</code> — Enlace al formulario de nueva solicitud</p>
        <p>👋 <code className="bg-white px-1 rounded">HOLA / AYUDA / MENU</code> — Menú de bienvenida</p>
      </div>

      <div className="border-t border-slate-100 pt-4">
        <p className="text-xs font-medium text-slate-700 mb-2">Enviar mensaje de prueba</p>
        <div className="flex gap-2">
          <input
            type="tel"
            value={testPhone}
            onChange={e => setTestPhone(e.target.value)}
            placeholder="+34 6XX XXX XXX"
            className="flex-1 border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#69C7EA]"
            data-testid="whatsapp-test-phone-input"
          />
          <button
            onClick={handleTest}
            disabled={sending}
            className="flex items-center gap-1.5 bg-[#25D366] hover:bg-[#20ba5a] text-white px-4 py-2 rounded-xl text-sm font-semibold disabled:opacity-60 transition-colors"
            data-testid="whatsapp-test-send-button"
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {sending ? '' : 'Probar'}
          </button>
        </div>
        <p className="text-xs text-slate-400 mt-2">
          Webhook URL: <code className="bg-slate-100 px-1 rounded">/api/webhooks/whatsapp</code>
          {' · '}Configurar en: Twilio Console → Messaging → WhatsApp → Senders → Edit Sender
        </p>
      </div>
    </div>
  );
}
